import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import KoobBadge from '@koob/ui/src/components/KoobBadge';
import { AdditionalServicesBandScopeEnum } from './AdditionalServicesBand';

// KD-3581 §4/§9 — one selected additional service, inside its band.
//
// Two rows, as measured on the mockup: the name, its amount and the remove
// control on the first, the application date and the traveller breakdown on the
// second. The name takes the whole first column and is never truncated — it
// wraps over as many lines as it needs, while the amount and the cross keep
// their own column so a long name cannot push them out. `KoobPill` was the
// obvious candidate and does not fit — it pins its content to a single 36px
// line.
//
// The border follows the accent of the band it sits in — orange on a day band,
// purple on a global one; the amount stays orange in both, like every amount of
// the panel (§9).
const chipVariants = cva(
  'flex w-full flex-col gap-1 rounded-[9px] border border-solid bg-white py-2 pl-3 pr-2.5',
  {
    variants: {
      scope: {
        [AdditionalServicesBandScopeEnum.DAY]: 'border-[#FBDCC3]',
        [AdditionalServicesBandScopeEnum.GLOBAL]: 'border-purple-200',
      },
    },
    defaultVariants: { scope: AdditionalServicesBandScopeEnum.GLOBAL },
  },
);

type AdditionalServiceChipProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className'
> &
  VariantProps<typeof chipVariants> & {
    name: string;
    /**
     * KD-3650 — which retention of the service this chip is, e.g. "Nº 2",
     * when the same service is retained several times on the day.
     *
     * Wording owned by the apps, like every other label here. So is the
     * decision to pass one at all: a service retained once is shown exactly as
     * before, and the caller omits the prop rather than passing "Nº 1".
     */
    occurrenceLabel?: React.ReactNode;
    /** Mandatory service — surfaced with a badge, wording owned by the apps. */
    isRequired?: boolean;
    requiredLabel?: React.ReactNode;
    /** Single date or range, already formatted from getServiceApplicationDate. */
    dateLabel?: React.ReactNode;
    /** Traveller breakdown, e.g. "2 ad.". */
    travellersLabel?: React.ReactNode;
    priceLabel?: React.ReactNode;
    /** KD-3744 — per-traveller share of the amount, shown under it. */
    perPaxLabel?: React.ReactNode;
    /** Omitted in read-only views, where a service cannot be removed. */
    onRemove?: React.MouseEventHandler<HTMLButtonElement>;
    removeLabel?: string;
  };

const AdditionalServiceChip = React.forwardRef<
  HTMLDivElement,
  AdditionalServiceChipProps
>(
  (
    {
      scope,
      name,
      occurrenceLabel,
      isRequired = false,
      requiredLabel,
      dateLabel,
      travellersLabel,
      priceLabel,
      perPaxLabel,
      onRemove,
      removeLabel,
      ...props
    },
    ref,
  ) => (
    <div ref={ref} className={chipVariants({ scope })} {...props}>
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 break-words text-[13px] leading-[1.35] text-[#16284F]">
          {/* Reads as a prefix of the name, and wraps with it: the name owns the
              whole first column and a long one must not push the number off. */}
          {occurrenceLabel && (
            <span className="mr-1.5 font-semibold text-slate-400">
              {occurrenceLabel}
            </span>
          )}

          {name}

          {isRequired && requiredLabel && (
            <span className="ml-1.5 align-middle">
              <KoobBadge color="amber" size="sm">
                {requiredLabel}
              </KoobBadge>
            </span>
          )}
        </span>

        {priceLabel && (
          <span className="flex shrink-0 flex-col items-end">
            <span className="whitespace-nowrap text-[12.5px] font-semibold text-orange-500">
              {priceLabel}
            </span>

            {perPaxLabel && (
              <span className="whitespace-nowrap text-[11px] text-slate-400">
                {perPaxLabel}
              </span>
            )}
          </span>
        )}

        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={removeLabel}
            title={removeLabel}
            className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-0 bg-[#F1F4F8] p-0 text-[11px] leading-none text-slate-500 transition-colors hover:bg-red-50 hover:text-red-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <i className="fa-light fa-xmark" />
          </button>
        )}
      </div>

      {(dateLabel || travellersLabel) && (
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-slate-400">
          {dateLabel && <span className="whitespace-nowrap">{dateLabel}</span>}

          {travellersLabel && (
            <span className="whitespace-nowrap">{travellersLabel}</span>
          )}
        </div>
      )}
    </div>
  ),
);

AdditionalServiceChip.displayName = 'AdditionalServiceChip';

export default AdditionalServiceChip;
